<template>
  <div class="space-y-6">
    <!-- 页面标题 -->
    <div>
      <h2 class="text-2xl font-semibold text-gray-700">Dashboard</h2>
      <div class="flex items-center mt-2">
        <a href="#" class="text-gray-600 hover:text-gray-800">首页</a>
        <span class="mx-2 text-gray-400">•</span>
        <span class="text-gray-600">Dashboard</span>
      </div>
    </div>
    
    <!-- 日期筛选 -->
    <div class="bg-white shadow rounded-md p-5 mb-6">
      <div class="flex flex-wrap items-center justify-between">
        <h3 class="text-lg font-medium text-gray-700 mb-3 md:mb-0">
          <i class="fas fa-calendar-alt mr-2 text-[#1aaa55]"></i>数据筛选
        </h3>
        <div class="flex flex-wrap items-end gap-4">
          <div class="relative min-w-[140px]">
            <label for="period" class="block text-sm font-medium text-gray-700 mb-1">时间范围</label>
            <FormSelect
              id="period"
              v-model="filters.period"
              :options="periodOptions"
              :disabled="!!filters.month"
            />
          </div>
          
          <button @click="loadDashboardData" type="button" class="mb-[15px] inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#1aaa55] hover:bg-[#159b4c] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1aaa55] shadow-sm">
            <i class="fas fa-filter mr-2"></i> 应用筛选
          </button>
        </div>
      </div>
    </div>
    
    <!-- 核心业务指标卡片 -->
    <div class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
      <!-- 总玩家数 -->
      <div class="bg-white overflow-hidden shadow rounded-md">
        <div class="p-5">
          <div class="flex items-center">
            <div class="flex-shrink-0 bg-blue-500 rounded-md p-3">
              <i class="fas fa-users text-white text-xl"></i>
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt class="text-sm font-medium text-gray-500 truncate">
                  总玩家数
                </dt>
                <dd class="metric-card-content">
                  <div v-if="loading" class="loading-indicator">
                    <div class="spinner"></div>
                  </div>
                  <div v-else class="flex items-baseline">
                    <p class="text-xl font-semibold text-gray-800">{{ stats.totalPlayers }}</p>
                    <p v-if="stats.newPlayerGrowth !== undefined" class="ml-2 flex items-baseline text-sm font-semibold" :class="stats.newPlayerGrowth >= 0 ? 'text-green-600' : 'text-red-600'">
                      <i :class="stats.newPlayerGrowth >= 0 ? 'fas fa-arrow-up mr-1' : 'fas fa-arrow-down mr-1'"></i>
                      {{ Math.abs(stats.newPlayerGrowth) }}%
                    </p>
                  </div>
                </dd>
              </dl>
            </div>
          </div>
        </div>
        <div class="bg-gray-50 px-5 py-3">
          <div class="text-sm">
            <a href="#" class="font-medium text-[#1aaa55] hover:text-[#159b4c]">
              查看所有玩家
            </a>
          </div>
        </div>
      </div>

      <!-- 总注单数 -->
      <div class="bg-white overflow-hidden shadow rounded-md">
        <div class="p-5">
          <div class="flex items-center">
            <div class="flex-shrink-0 bg-[#1aaa55] rounded-md p-3">
              <i class="fas fa-file-invoice text-white text-xl"></i>
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt class="text-sm font-medium text-gray-500 truncate">
                  当月注单数
                </dt>
                <dd class="metric-card-content">
                  <div v-if="loading" class="loading-indicator">
                    <div class="spinner"></div>
                  </div>
                  <div v-else class="flex items-baseline">
                    <p class="text-xl font-semibold text-gray-800">{{ formatNumber(stats.totalBets) }}</p>
                    <p v-if="stats.betGrowth !== undefined" class="ml-2 flex items-baseline text-sm font-semibold" :class="stats.betGrowth >= 0 ? 'text-green-600' : 'text-red-600'">
                      <i :class="stats.betGrowth >= 0 ? 'fas fa-arrow-up mr-1' : 'fas fa-arrow-down mr-1'"></i>
                      {{ Math.abs(stats.betGrowth) }}%
                    </p>
                  </div>
                </dd>
              </dl>
            </div>
          </div>
        </div>
        <div class="bg-gray-50 px-5 py-3">
          <div class="text-sm">
            <a href="#" class="font-medium text-[#1aaa55] hover:text-[#159b4c]">
              查看所有注单
            </a>
          </div>
        </div>
      </div>

      <!-- 今日收入 -->
      <div class="bg-white overflow-hidden shadow rounded-md">
        <div class="p-5">
          <div class="flex items-center">
            <div class="flex-shrink-0 bg-[#1aaa55] rounded-md p-3">
              <i class="fas fa-chart-line text-white text-xl"></i>
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt class="text-sm font-medium text-gray-500 truncate">
                  今日收入
                </dt>
                <dd class="metric-card-content">
                  <div v-if="loading" class="loading-indicator">
                    <div class="spinner"></div>
                  </div>
                  <div v-else class="flex items-baseline">
                    <p class="text-xl font-semibold text-gray-800">¥{{ formatNumber(stats.totalRevenue) }}</p>
                    <p v-if="stats.revenueGrowth !== undefined" class="ml-2 flex items-baseline text-sm font-semibold" :class="stats.revenueGrowth >= 0 ? 'text-green-600' : 'text-red-600'">
                      <i :class="stats.revenueGrowth >= 0 ? 'fas fa-arrow-up mr-1' : 'fas fa-arrow-down mr-1'"></i>
                      {{ Math.abs(stats.revenueGrowth) }}%
                    </p>
                  </div>
                </dd>
              </dl>
            </div>
          </div>
        </div>
        <div class="bg-gray-50 px-5 py-3">
          <div class="text-sm">
            <a href="#" class="font-medium text-[#1aaa55] hover:text-[#159b4c]">
              查看财务报表
            </a>
          </div>
        </div>
      </div>

      <!-- 异常注单 -->
      <div class="bg-white overflow-hidden shadow rounded-md">
        <div class="p-5">
          <div class="flex items-center">
            <div class="flex-shrink-0 bg-red-500 rounded-md p-3">
              <i class="fas fa-exclamation-triangle text-white text-xl"></i>
            </div>
            <div class="ml-5 w-0 flex-1">
              <dl>
                <dt class="text-sm font-medium text-gray-500 truncate">
                  待处理异常
                </dt>
                <dd class="metric-card-content">
                  <div v-if="loading" class="loading-indicator">
                    <div class="spinner"></div>
                  </div>
                  <div v-else class="flex items-baseline">
                    <p class="text-xl font-semibold text-gray-800">{{ stats.pendingAbnormalBets || 0 }}</p>
                  </div>
                </dd>
              </dl>
            </div>
          </div>
        </div>
        <div class="bg-gray-50 px-5 py-3">
          <div class="text-sm">
            <a href="#" class="font-medium text-[#1aaa55] hover:text-[#159b4c]">
              处理异常注单
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- 图表区域 -->
    <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <!-- 盈利趋势图 -->
      <div class="bg-white shadow rounded-md p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium text-gray-700">盈利趋势</h3>
        </div>
        <div class="chart-container">
          <canvas ref="profitChartRef"></canvas>
        </div>
      </div>

      <!-- 注单趋势图 -->
      <div class="bg-white shadow rounded-md p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium text-gray-700">注单趋势</h3>
        </div>
        <div class="chart-container">
          <canvas ref="betChartRef"></canvas>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-6 lg:grid-cols-3 mt-6">
      <!-- 最近投注 -->
      <div class="lg:col-span-2 bg-white rounded-lg shadow">
        <div class="px-6 py-5 border-b border-gray-200">
          <h3 class="text-lg font-medium leading-6 text-gray-900">最近投注</h3>
        </div>
        <div class="p-6">
          <div v-if="loading" class="loading-indicator">
            <div class="spinner"></div>
          </div>
          <div v-else class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th class="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">玩家</th>
                  <th class="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">游戏</th>
                  <th class="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">投注额</th>
                  <th class="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                  <th class="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">时间</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="bet in recentBets" :key="bet.id">
                  <td class="px-4 py-3 whitespace-nowrap">
                    <div class="text-sm font-medium text-gray-900">{{ bet.username }}</div>
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap">
                    <div class="text-sm text-gray-900">{{ bet.vendorName }}</div>
                    <div class="text-xs text-gray-500">{{ bet.gameName }}</div>
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap">
                    <div class="text-sm text-gray-900">¥{{ formatNumber(bet.amount) }}</div>
                    <div :class="`text-xs ${bet.winAmount >= 0 ? 'text-green-500' : 'text-red-500'}`">
                      {{ bet.winAmount >= 0 ? '+' : '' }}¥{{ formatNumber(bet.winAmount) }}
                    </div>
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap">
                    <span :class="`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getBetStatusClass(bet.status)}`">
                      {{ bet.statusText }}
                    </span>
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {{ formatDateTime(bet.createdAt) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- 热门游戏 -->
      <div class="bg-white rounded-lg shadow">
        <div class="px-6 py-5 border-b border-gray-200">
          <h3 class="text-lg font-medium leading-6 text-gray-900">热门游戏</h3>
        </div>
        <div class="p-6">
          <div v-if="loading" class="loading-indicator">
            <div class="spinner"></div>
          </div>
          <div v-else>
            <div 
              v-for="(game, index) in gameRanking" 
              :key="game.id" 
              class="flex items-center py-3" 
              :class="index !== gameRanking.length - 1 ? 'border-b border-gray-200' : ''"
            >
              <div class="flex-shrink-0 w-8 text-center">
                <span :class="`text-sm font-medium ${index < 3 ? 'text-[#1aaa55]' : 'text-gray-500'}`">#{{ index + 1 }}</span>
              </div>
              <div class="ml-4 flex-grow">
                <h4 class="text-sm font-medium text-gray-900">{{ game.gameName }}</h4>
                <p class="text-xs text-gray-500">{{ game.vendorName }}</p>
              </div>
              <div class="ml-2">
                <span class="text-sm font-medium text-gray-900">{{ formatNumber(game.betCount) }}</span>
                <span class="text-xs text-gray-500">注单</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive } from 'vue';
import AdminApiClient from '@/utils/admin-api-client';
import Chart from 'chart.js/auto';
import FormSelect from '@/components/common/FormSelect.vue';

const apiClient = new AdminApiClient();
const loading = ref(true);
const profitChartRef = ref(null);
const betChartRef = ref(null);
let profitChart = null;
let betChart = null;

// 数据筛选
const filters = reactive({
  month: '',
  period: '7'
});

// 月份选项
const months = [
  { label: '一月', value: '01' },
  { label: '二月', value: '02' },
  { label: '三月', value: '03' },
  { label: '四月', value: '04' },
  { label: '五月', value: '05' },
  { label: '六月', value: '06' },
  { label: '七月', value: '07' },
  { label: '八月', value: '08' },
  { label: '九月', value: '09' },
  { label: '十月', value: '10' },
  { label: '十一月', value: '11' },
  { label: '十二月', value: '12' },
];

// 时间范围选项
const periodOptions = [
  { label: '最近7天', value: '7' },
  { label: '最近15天', value: '15' },
  { label: '最近30天', value: '30' },
];

// 统计数据
const stats = reactive({
  totalPlayers: 0,
  newPlayerGrowth: 0,
  totalBets: 0,
  betGrowth: 0,
  totalRevenue: 0,
  revenueGrowth: 0,
  pendingAbnormalBets: 0
});

// 最近投注
const recentBets = ref([]);

// 游戏排名
const gameRanking = ref([]);

// 图表数据
const chartData = reactive({
  profitData: {
    labels: [],
    profits: []
  },
  betData: {
    labels: [],
    counts: []
  }
});

onMounted(() => {
  loadDashboardData();
});

// 加载仪表盘数据
const loadDashboardData = async () => {
  loading.value = true;
  try {
    // 使用AdminApiClient调用API
    let apiSuccess = false;
    try {
      console.log('正在获取仪表盘数据，参数:', { month: filters.month, period: filters.period });
      const response = await apiClient.getDashboardData({
        month: filters.month,
        period: filters.period
      });
      
      console.log('仪表盘API响应:', response);
      
      // 如果有实际数据，使用实际数据
      if (response && response.success) {
        apiSuccess = true;
        
        // 解析API返回的数据
        const data = response.data || {};
        
        // 更新统计数据 (从其他地方获取或使用静态数据)
        stats.totalPlayers = data.total_players; // 这些值应该从API获取
        stats.newPlayerGrowth = 0;
        stats.totalBets = data.current_month_bets;
        stats.betGrowth = 0;
        stats.totalRevenue = data.today_income;
        stats.revenueGrowth = 0;
        stats.pendingAbnormalBets = data.pending_abnormal_bets;
        
        // 更新盈利趋势图数据
        if (data.profit_trend) {
          chartData.profitData.labels = data.profit_trend.dates || [];
          chartData.profitData.profits = data.profit_trend.amounts || [];
        }
        
        // 更新投注趋势图数据
        if (data.bet_trend) {
          chartData.betData.labels = data.bet_trend.dates || [];
          chartData.betData.counts = data.bet_trend.counts || [];
        }
        
        // 更新最近投注
        if (Array.isArray(data.recent_bets)) {
          // 为每个注单添加必要的显示文本
          recentBets.value = data.recent_bets.map(bet => {
            // 解析API返回的注单数据
            return {
              id: bet.id || bet.bet_id,
              username: bet.username || bet.player_name,
              gameName: bet.game_name || '未知游戏',
              vendorName: bet.vendor_name || '未知供应商',
              amount: bet.amount || bet.bet_amount || 0,
              winAmount: bet.win_amount || 0,
              status: bet.status || 'pending',
              statusText: bet.status_text,
              createdAt: bet.created_at ? new Date(bet.created_at) : new Date()
            };
          });
        }
        
        // 更新游戏排名
        if (Array.isArray(data.game_ranking)) {
          gameRanking.value = data.game_ranking.map(game => {
            return {
              id: game.id || game.game_id,
              gameName: game.game_name || '未知游戏',
              vendorName: game.vendor_name || '未知供应商',
              betCount: game.bet_count || 0
            };
          });
        }
      } else {
        console.warn('API返回成功但没有有效数据', response);
      }
    } catch (apiError) {
      console.error('API调用失败，详细错误:', apiError);
    }
    // 初始化图表
    initCharts();
  } catch (error) {
    console.error('加载仪表盘数据失败', error);
  } finally {
    loading.value = false;
  }
};

// 初始化图表
const initCharts = () => {
  // 销毁现有图表
  if (profitChart) {
    profitChart.destroy();
  }
  if (betChart) {
    betChart.destroy();
  }
  
  // 盈利趋势图
  if (profitChartRef.value) {
    const ctx = profitChartRef.value.getContext('2d');
    profitChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: chartData.profitData.labels,
        datasets: [{
          label: '平台盈利 (¥)',
          data: chartData.profitData.profits,
          backgroundColor: 'rgba(26, 170, 85, 0.1)',
          borderColor: 'rgba(26, 170, 85, 1)',
          borderWidth: 2,
          fill: true,
          tension: 0.4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                return `¥${formatNumber(context.raw)}`;
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function(value) {
                return `¥${formatNumber(value)}`;
              }
            }
          }
        }
      }
    });
  }
  
  // 注单趋势图
  if (betChartRef.value) {
    const ctx = betChartRef.value.getContext('2d');
    betChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: chartData.betData.labels,
        datasets: [{
          label: '注单数',
          data: chartData.betData.counts,
          backgroundColor: 'rgba(59, 130, 246, 0.7)',
          borderRadius: 4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              precision: 0
            }
          }
        }
      }
    });
  }
};

// 格式化数字
const formatNumber = (num) => {
  if (num === undefined || num === null) return '0';
  return num.toLocaleString('zh-CN', { maximumFractionDigits: 2, minimumFractionDigits: 0 });
};

// 格式化日期时间
const formatDateTime = (date) => {
  if (!date) return '';
  if (typeof date === 'string') {
    date = new Date(date);
  }
  
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  
  if (diffMins < 60) {
    return `${diffMins} 分钟前`;
  } else if (diffMins < 24 * 60) {
    return `${Math.floor(diffMins / 60)} 小时前`;
  } else {
    return date.toLocaleString('zh-CN', {
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
};

// 获取投注状态样式
const getBetStatusClass = (status) => {
  switch (status) {
    case 0:
      return 'bg-yellow-100 text-yellow-800';
    case 1:
      return 'bg-green-100 text-green-800';
    case 2:
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

// 获取状态文本
const getStatusText = (status) => {
  const statusMap = {
    'settled': '已结算',
    'pending': '待结算',
    'canceled': '已取消'
  };
  return statusMap[status] || status;
};
</script>

<style scoped>
.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border-left-color: #09f;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.dashboard-card {
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s;
}

.dashboard-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.chart-container {
  height: 280px;
  position: relative;
}

.metric-value {
  font-size: 1.75rem;
  font-weight: 600;
  line-height: 2rem;
}

.metric-label {
  font-size: 0.875rem;
  color: #6b7280;
}

.growth-indicator {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.125rem 0.375rem;
  border-radius: 9999px;
}

.growth-positive {
  background-color: rgba(16, 185, 129, 0.1);
  color: rgb(16, 185, 129);
}

.growth-negative {
  background-color: rgba(239, 68, 68, 0.1);
  color: rgb(239, 68, 68);
}

.table-header {
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.table-row:hover {
  background-color: #f9fafb;
}

.table-row td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.ranking-item {
  padding: 0.5rem 0;
  border-bottom: 1px solid #e5e7eb;
}

.ranking-item:last-child {
  border-bottom: none;
}

.ranking-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background-color: #e5e7eb;
  color: #4b5563;
  border-radius: 50%;
  font-size: 0.75rem;
  font-weight: 600;
}

.ranking-number-top {
  background-color: #fef3c7;
  color: #d97706;
}

.action-link {
  color: #3b82f6;
  font-size: 0.875rem;
  font-weight: 500;
  transition: color 0.2s;
}

.action-link:hover {
  color: #2563eb;
  text-decoration: underline;
}
</style> 